import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { usePersistedFilter, FILTER_KEYS } from '../../../utils/filterPersistence';
import { DEFAULT_STATUS_FILTER, DEFAULT_ROWS_PER_PAGE } from '../utils/ticketConstants';
import { matchesSearch } from '../utils/ticketTransformers';

/**
 * Hook for managing ticket filter state with persistence
 */
export const useTicketFilters = () => {
  // Persisted filter states
  const [page, setPage] = usePersistedFilter(FILTER_KEYS.TICKET.PAGE, 0);
  const [rawRowsPerPage, setRowsPerPage] = usePersistedFilter(FILTER_KEYS.TICKET.ROWS_PER_PAGE, DEFAULT_ROWS_PER_PAGE);
  // Allow -1 for "All" rows, otherwise ensure valid positive number
  const rowsPerPage = (rawRowsPerPage === -1 || (rawRowsPerPage && rawRowsPerPage > 0)) ? rawRowsPerPage : DEFAULT_ROWS_PER_PAGE;
  const [statusFilter, setStatusFilter] = usePersistedFilter(FILTER_KEYS.TICKET.STATUS_FILTER, DEFAULT_STATUS_FILTER);
  const [urgencyFilter, setUrgencyFilter] = usePersistedFilter(FILTER_KEYS.TICKET.URGENCY_FILTER, '');
  const [supportTypeFilter, setSupportTypeFilter] = usePersistedFilter(FILTER_KEYS.TICKET.SUPPORT_TYPE_FILTER, '');
  const [searchFilter, setSearchFilter] = usePersistedFilter(FILTER_KEYS.TICKET.SEARCH, '');
  const [nextStepFilter, setNextStepFilter] = usePersistedFilter(FILTER_KEYS.TICKET.NEXT_STEP_FILTER, '');

  // Local search input state for debouncing
  const [searchInputValue, setSearchInputValue] = useState(searchFilter);
  const searchDebounceRef = useRef(null);

  // Sorting state
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');

  // Combine into filter object - ensure proper types
  const filter = useMemo(() => ({
    status: Array.isArray(statusFilter) ? statusFilter : [],
    urgency: urgencyFilter || '',
    support_type: supportTypeFilter || '',
    search: typeof searchFilter === 'string' ? searchFilter : '',
    next_step: typeof nextStepFilter === 'string' ? nextStepFilter : ''
  }), [statusFilter, urgencyFilter, supportTypeFilter, searchFilter, nextStepFilter]);

  // Handle filter change
  const handleFilterChange = useCallback((name, value) => {
    setPage(0); // Reset to first page when filters change
    switch (name) {
      case 'status':
        setStatusFilter(value);
        break;
      case 'urgency':
        setUrgencyFilter(value);
        break;
      case 'support_type':
        setSupportTypeFilter(value);
        break;
      case 'search':
        setSearchFilter(value);
        break;
      case 'next_step':
        setNextStepFilter(value);
        break;
      default:
        break;
    }
  }, [setPage, setStatusFilter, setUrgencyFilter, setSupportTypeFilter, setSearchFilter, setNextStepFilter]);

  // Reset all filters
  const handleResetFilter = useCallback(() => {
    setPage(0);
    setStatusFilter(DEFAULT_STATUS_FILTER);
    setUrgencyFilter('');
    setSupportTypeFilter('');
    setSearchFilter('');
    setNextStepFilter('');
    setSearchInputValue('');
  }, [setPage, setStatusFilter, setUrgencyFilter, setSupportTypeFilter, setSearchFilter, setNextStepFilter]);

  // Handle search input with debounce
  const handleSearchInputChange = useCallback((value) => {
    setSearchInputValue(value);

    // Clear existing timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Set new debounced update
    searchDebounceRef.current = setTimeout(() => {
      handleFilterChange('search', value);
    }, 300);
  }, [handleFilterChange]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // Handle sort
  const handleRequestSort = useCallback((property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  }, [orderBy, order, setPage]);

  // Handle pagination
  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, [setPage]);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, [setRowsPerPage, setPage]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return filter.status.length > 0 ||
           filter.urgency !== '' ||
           filter.support_type !== '' ||
           filter.next_step !== '';
  }, [filter]);

  // Get active filter summary
  const getActiveFilterSummary = useCallback(() => {
    const activeFilters = [];
    if (filter.status.length > 0) activeFilters.push(`Status: ${filter.status.join(', ')}`);
    if (filter.urgency) activeFilters.push(`Urgency: ${filter.urgency}`);
    if (filter.support_type) activeFilters.push(`Type: ${filter.support_type}`);
    if (filter.next_step) activeFilters.push(`Next Step: ${filter.next_step}`);
    return activeFilters;
  }, [filter]);

  return {
    // Filter state
    filter,
    page,
    rowsPerPage,
    orderBy,
    order,
    searchInputValue,

    // Filter actions
    handleFilterChange,
    handleResetFilter,
    handleSearchInputChange,
    handleRequestSort,
    handleChangePage,
    handleChangeRowsPerPage,

    // Filter info
    hasActiveFilters,
    getActiveFilterSummary
  };
};

export default useTicketFilters;
